import PropTypes from 'prop-types';
import React from 'react';
import MessageComposer from '../MessageComposer';

const Step2MessageComposer = ({
  creditData,
  setCreditData,
  trackName = 'Your Track',
  type = 'credit',
}) => {
  const handleChannelChange = (channel) => {
    setCreditData((prev) => ({ ...prev, messageChannel: channel }));
  };

  const handleMessageChange = (message) => {
    setCreditData((prev) => ({ ...prev, messageContent: message }));
  };

  const handleTitleChange = (title) => {
    setCreditData((prev) => ({ ...prev, messageTitle: title }));
  };

  const replaceVariables = (text) => {
    if (!text) return text;

    // For refunds, determine the amounts based on the method used
    let refundAmount = 0;
    let creditAmount = 0;

    if (type === 'refund') {
      // Check which method is being used
      if (creditData.creditMethod === 'tickets') {
        // Ticket-based refund: use dynamic amounts from backend
        refundAmount = parseFloat(creditData.dynamicRefundAmount) || 0;
        creditAmount = parseFloat(creditData.creditRestored) || 0;
      } else if (creditData.creditMethod === 'custom') {
        // Custom amount refund: use customAmount directly (not amount, which syncs later)
        refundAmount = parseFloat(creditData.customAmount) || 0;
        creditAmount = 0;
      }
    } else {
      // For credits, check the method
      if (creditData.creditMethod === 'custom') {
        // Custom credit: use customAmount directly
        creditAmount = parseFloat(creditData.customAmount) || 0;
      } else {
        // Ticket-based credit: use amount field
        creditAmount = parseFloat(creditData.amount) || 0;
      }
      refundAmount = 0;
    }

    return text
      .replace(/\{\{first_name\}\}/g, creditData.customerName.split(' ')[0])
      .replace(/\{\{credit_amount\}\}/g, `$${creditAmount.toFixed(2)}`)
      .replace(/\{\{refund_amount\}\}/g, `$${refundAmount.toFixed(2)}`)
      .replace(
        /\{\{expiration\}\}/g,
        creditData.expiration
          ? new Date(creditData.expiration).toLocaleDateString()
          : 'no expiration'
      )
      .replace(/\{\{track_name\}\}/g, trackName);
  };

  // Define quick variables for credit context
  const creditVariables = [
    {
      label: 'First Name',
      value: '{{first_name}}',
      icon: 'M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    },
    {
      label: 'Credit Amount',
      value: '{{credit_amount}}',
      icon: 'M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z',
    },
    {
      label: 'Refund Amount',
      value: '{{refund_amount}}',
      icon: 'M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z',
    },
    {
      label: 'Expiration',
      value: '{{expiration}}',
      icon: 'M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zM3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z',
    },
    {
      label: 'Track',
      value: '{{track_name}}',
      icon: 'M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
    },
  ];

  return (
    <MessageComposer
      selectedChannel={creditData.messageChannel}
      setSelectedChannel={handleChannelChange}
      message={creditData.messageContent}
      setMessage={handleMessageChange}
      pushTitle={creditData.messageTitle}
      setPushTitle={handleTitleChange}
      variables={creditVariables}
      replaceVariables={replaceVariables}
      context="credit"
      availableChannels={['SMS']} // Only allow SMS for credit messages
      hideCharacterCount={true} // Hide character count and segment info in credit flow
    />
  );
};

Step2MessageComposer.propTypes = {
  creditData: PropTypes.object.isRequired,
  setCreditData: PropTypes.func.isRequired,
  trackName: PropTypes.string,
  type: PropTypes.oneOf(['credit', 'refund']),
};

export default Step2MessageComposer;
